import { FunctionComponent, useEffect, useState, useRef } from "react";
import { addNoteFirebase, changeLeadStage, fetchConstants, } from "../../../Services/contacts";
import { connect, useDispatch } from "react-redux";
import { showSnackbarAction } from "../../../Redux/actions";
import { useHistory } from "react-router-dom";
import TextInput from "../../TextInput/InputText";
import { themeColors } from "../../theme";
import "../../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import DropDown from "../../../Screens/LeadDistributorLogic/Dropdown"
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";
import { styled } from "@mui/material/styles";
import { TextField } from "@mui/material";


// Primary theme color used in textarea styling
const primaryColor = "#292744";

/**
 * Styled MUI TextArea used for "Other Lost Reason"
 */
const CustomTextArea = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    fontSize: "var(--font-size-large)",
    fontFamily: "var(--font-family-main)",
    "& fieldset": {
      borderColor: "#bfc7d5",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`
    },
  },
  "& .MuiInputBase-input": {
    padding: "14px",
    fontSize: 16,
    color: "#222",
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    fontSize: 15,
    color: primaryColor,
  },
});

type props = {
  contactDetail: any; // Contains details of selected contact
  close: () => void; // Callback to close modal
  open: boolean; // Controls modal visibility
  tasksData: any[]; // Relevant task data for lead stage changes
  notes: any[]; // All existing notes for this contact
  user: any; // Logged-in user details
  theme: any; // Light / dark theme flag
};

/**
 * LostModal  
 * Displays UI to mark a lead as LOST along with:
 *  - Lost reason selection
 *  - Optional "Other" reason input
 *  - Adding note
 *  - Updating lead stage in Firebase
 */
const LostModal: FunctionComponent<props> = ({
  contactDetail,
  close,
  open,
  tasksData,
  notes,
  user,
  theme
}) => {
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const dispatcher = useDispatch();
  const history = useHistory();
  
  // Refs for note input and other reason input
  const otherRef: any = useRef();
  const noteRef: any = useRef();

   /** Loader flag for submit button */
  const [load, setLoad] = useState(false);

  /** Stores currently selected Lost Reason */
  const [lostSelected, setLostSelected] = useState<any>({ label: contactDetail.lost_reason ? contactDetail.lost_reason : "Select", value: contactDetail.lost_reason ? contactDetail.lost_reason : "Select" });
  
   /** Constants fetched from server for dropdown lists */
  const [propertyTypeList, setPropertyTypeList] = useState<any[]>([]);
  const [propertyStageList, setPropertyStageList] = useState<any[]>([]);
  const [nextFollowUpList, setnextFollowUpList] = useState<any[]>([]);
  const [notIntReasonList, setNotIntReasonList] = useState<any[]>([]);
  const [lostReasonList, setLostReasonList] = useState<any[]>([]);

  /** Value for "Other Lost Reason" input */
  const [other, setOther] = useState("");

    /**
   * Fetch constant values only once when modal loads.
   * These values populate dropdown options.
   */
  useEffect(() => {
    const unsubConst = fetchConstants(
      (data) => setNotIntReasonList(data),
      (data) => setLostReasonList(data),
      (data) => setPropertyStageList(data),
      (data) => setPropertyTypeList(data),
      (data) => setnextFollowUpList(data)
    );
    return () => {
      unsubConst();
    };
  }, []);

    /**
   * Handles submission logic:
   * 1. Validate Lost Reason
   * 2. Save Note (if any)
   * 3. Push LOST update to Firebase
   * 4. Update UI + Navigate back
   */
  const onSubmit = () => {
    if (lostSelected.value === "Select") {
      dispatcher(showSnackbarAction("Please Select Lost Reason", "error"));
    } else if (lostSelected.value === "Other" && other === "") {
      dispatcher(showSnackbarAction("Please Mention Other Lost Reason", "error"));
    } else {
       // If user added a note, save it
      if (noteRef.current.value.length !== 0) {
        addNoteFirebase(
          contactDetail.contactId,
          notes,
          noteRef.current.value,
          () => { },
          dispatcher,
          () => { },
          user.user_email
        );
      }
      // Update Lead Stage to LOST
      changeLeadStage(
        contactDetail.contactId,
        {
          lost_reason: lostSelected.value,
          stage: "LOST",
          other_lost_reason: other === "" ? "" : other,
        },
        (data) => setLoad(data),
        dispatcher,
        close,
        history,
        tasksData
      );
    }
  };

  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      width: 400,
      maxWidth: 400,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 380,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
           {/* Form Fields */}
          <form style={{ width: "100%" }}>
            <Box sx={{ mb: 2 }}>
              <h3
                style={{
                  color: theme ? "#272944" : "#fff",
                  fontFamily: "var(--font-family-main)",
                  fontSize: "var(--font-size-large)",
                  marginBottom: 24,
                  textAlign: "center",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                Lost Details
              </h3>
            </Box>
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid item xs={12} sm={12}>
                <DropDown
                  title="Lost Reason"
                  option={lostReasonList}
                  selectedValue={lostSelected.value === "Select" ? "" : lostSelected}
                  setSelectedValue={(value) => {
                    setLostSelected(value);
                  }}
                  required={true}
                />
              </Grid>
              {lostSelected.value === "Other" && (
                <Grid item xs={12} sm={12}>
                  <CustomTextArea
                    fullWidth
                    label="Enter Other Reason"
                    variant="outlined"
                    multiline
                    rows={1}
                    ref={otherRef}
                    onChange={(e) => setOther(e.target.value)}
                    value={other}
                    InputLabelProps={{ style: { color: "#555", fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-medium)" } }}
                    InputProps={{ style: { color: '#222', fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-medium)" } }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={12}>
                <TextInput
                  title="Note"
                  style={{ backgroundColor: "#fff" }}
                  ref={noteRef}
                />
              </Grid>
            </Grid>

            {/* Modal Footer Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                  width: "100%",
                }}
              >
                <Box style={{ marginRight: 8 }}>
                  <StyledCancelButton onClick={close} isMobile={isMobile}>
                    Cancel
                  </StyledCancelButton>
                </Box>
                <Box>
                  <StyledSubmitButton
                    onClick={onSubmit}
                    themeMode={themeMUI.palette.mode}
                    isMobile={isMobile}
                  >
                    {load ? (
                      <span style={{ display: "flex", justifyContent: "center" }}>
                        <Circles color="#fff" height={20} width={20} />
                      </span>
                    ) : (
                      <>Submit</>
                    )}
                  </StyledSubmitButton>
                </Box>
              </Box>
            </Grid>
          </form>
        </Box>
      </Modal >
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(LostModal);
